import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, AlertCircle, CheckCircle2, MessageCircle } from 'lucide-react';
import { submitLead } from '../services/api';
import { PHONE_NUMBER, WHATSAPP_NUMBER } from '../utils/constants';
import toast from 'react-hot-toast';
import './Contact.css';

const PHONE_RE = /^[0-9]{10}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const [form, setForm]       = useState({ name:'', phone:'', countryCode:'+91', email:'', message:'' });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);

  const set = (k, v) => { setForm(p=>({...p,[k]:v})); if (errors[k]) setErrors(p=>({...p,[k]:''})); };

  const validate = () => {
    const e = {};
    if (!form.name.trim())                              e.name    = 'Full name is required';
    if (!form.phone.trim())                             e.phone   = 'Phone number is required';
    else if (!PHONE_RE.test(form.phone))                e.phone   = 'Enter a valid 10-digit mobile number';
    if (form.email && !EMAIL_RE.test(form.email))       e.email   = 'Enter a valid email address';
    if (!form.message.trim())                           e.message = 'Please write a message';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      await submitLead({ name:form.name.trim(), phone: form.countryCode + form.phone, email:form.email.trim()||null, message:form.message.trim() });
      setDone(true);
      toast.success("Message sent! We'll contact you within 24 hours.");
    } catch {
      toast.error('Failed to send. Please call us directly.');
    } finally { setLoading(false); }
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <span className="section-label">Get In Touch</span>
          <h1 className="section-title on-dark">Contact Us</h1>
          <p className="contact-hero__sub">We're here to help with all your real estate needs</p>
        </div>
      </section>

      <section className="contact-body">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <span className="section-label">Reach Us</span>
              <h2 className="section-title">Let's Start a Conversation</h2>
              <div style={{height:'3px',width:'48px',background:'var(--primary)',margin:'14px 0 20px'}}/>
              <p className="contact-info__lead">Whether you're a home buyer, investor, or looking for a channel partnership - our team is ready to assist you.</p>
              <div className="contact-items">
                <div className="ci"><div className="ci__icon"><Phone size={17}/></div><div className="ci__text"><strong>Call Us</strong><a href={"tel:"+PHONE_NUMBER}>{PHONE_NUMBER}</a><span>Mon–Sat, 9 AM – 7 PM</span></div></div>
                <div className="ci"><div className="ci__icon"><MessageCircle size={17}/></div><div className="ci__text"><strong>WhatsApp</strong><a href={"https://wa.me/"+WHATSAPP_NUMBER} target="_blank" rel="noreferrer">Chat instantly</a><span>Quick responses</span></div></div>
                <div className="ci"><div className="ci__icon"><Mail size={17}/></div><div className="ci__text"><strong>Email</strong><a href="mailto:info@smartpropertysolutions.in">info@smartpropertysolutions.in</a><span>Reply within 24 hrs</span></div></div>
                <div className="ci"><div className="ci__icon"><MapPin size={17}/></div><div className="ci__text"><strong>Office</strong><span>H-32, Office No. 304, Sector 63, Noida</span></div></div>
                <div className="ci"><div className="ci__icon"><Clock size={17}/></div><div className="ci__text"><strong>Hours</strong><span>Mon–Sat: 9 AM – 7 PM</span><span>Sunday: 10 AM – 4 PM</span></div></div>
              </div>
            </div>

            <div className="contact-form-card">
              {done ? (
                <div className="contact-success">
                  <CheckCircle2 size={48} color="#16a34a"/>
                  <h3>Thank You!</h3>
                  <p>Your message has been received. We'll contact you within 24 hours.</p>
                  <button className="btn-primary" onClick={()=>setDone(false)} style={{marginTop:'20px'}}>Send Another</button>
                </div>
              ) : (
                <>
                  <div className="cf-hdr"><h3>Send Us a Message</h3><p>Fill in the form and we'll get back to you shortly</p></div>
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="cf-row">
                      <div className="cf-g">
                        <input type="text" placeholder="Full Name *" value={form.name} onChange={e=>set('name',e.target.value)} className={"cf-in"+(errors.name?' err':'')}/>
                        {errors.name&&<p className="cf-err"><AlertCircle size={11}/> {errors.name}</p>}
                      </div>
                      <div className="cf-g">
                        <div style={{display:'flex',gap:'6px'}}>
                          <select value={form.countryCode} onChange={e=>set('countryCode',e.target.value)} style={{height:'44px',padding:'0 8px',border:'1.5px solid #e5e7eb',borderRadius:'6px',fontSize:'13px',background:'#f9fafb',flexShrink:0,width:'90px'}}>
                            <option value="+91">🇮🇳 +91</option>
                            <option value="+1">🇺🇸 +1</option>
                            <option value="+44">🇬🇧 +44</option>
                            <option value="+971">🇦🇪 +971</option>
                            <option value="+65">🇸🇬 +65</option>
                            <option value="+61">🇦🇺 +61</option>
                            <option value="+49">🇩🇪 +49</option>
                            <option value="+33">🇫🇷 +33</option>
                          </select>
                          <div style={{flex:1}}>
                            <input type="tel" placeholder="10-digit mobile *" value={form.phone} onChange={e=>set('phone',e.target.value.replace(/[^0-9]/g,''))} className={"cf-in"+(errors.phone?' err':'')} maxLength={10} style={{width:'100%'}}/>
                          </div>
                        </div>
                        {errors.phone&&<p className="cf-err"><AlertCircle size={11}/> {errors.phone}</p>}
                      </div>
                    </div>
                    <div className="cf-g">
                      <input type="email" placeholder="Email Address (optional)" value={form.email} onChange={e=>set('email',e.target.value)} className={"cf-in"+(errors.email?' err':'')}/>
                      {errors.email&&<p className="cf-err"><AlertCircle size={11}/> {errors.email}</p>}
                    </div>
                    <div className="cf-g">
                      <textarea rows={5} placeholder="Your message *" value={form.message} onChange={e=>set('message',e.target.value)} className={"cf-in"+(errors.message?' err':'')}/>
                      {errors.message&&<p className="cf-err"><AlertCircle size={11}/> {errors.message}</p>}
                    </div>
                    <button type="submit" className="cf-sub" disabled={loading}>
                      <Send size={14}/> {loading?'Sending...':'Send Message'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="contact-map">
        <iframe title="Office" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.696984668088!2d77.37193!3d28.6259!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5a1e0c87ced%3A0x9f7f78c8e9e9e9e9!2sSector%2063%2C%20Noida!5e0!3m2!1sen!2sin!4v1" width="100%" height="380" style={{border:0}} allowFullScreen loading="lazy"/>
      </div>
    </div>
  );
};
export default Contact;
